// Convert GitHub-style raw HTML <details>/<summary> into Discourse [details]
// BBCode so PrettyText and the rich editor both render a collapsible section.
// Inline style attributes stay rejected (xss allow-lister does not permit them).

const CLOSE_TAG = "</details>";

export function htmlDetailsToBbcode(src) {
  const { text, restore } = maskFencedCode(src);
  return restore(convertDetails(text));
}

export function setup(helper) {
  helper.allowList([
    "details",
    "details[open]",
    "details.elided",
    "summary",
    "summary[title]",
  ]);

  helper.registerPlugin((md) => {
    md.core.ruler.before("normalize", "html-details-to-bbcode", (state) => {
      state.src = htmlDetailsToBbcode(state.src);
    });
  });
}

function maskFencedCode(src) {
  const lines = src.split("\n");
  const fences = [];
  const out = [];
  let fence = null;
  let buf = [];

  for (const line of lines) {
    if (fence) {
      buf.push(line);
      const close = line.match(/^( {0,3})(`{3,}|~{3,})[ \t]*$/);
      if (
        close &&
        close[2][0] === fence.char &&
        close[2].length >= fence.len
      ) {
        fences.push(buf.join("\n"));
        out.push(`\0FENCE${fences.length - 1}\0`);
        fence = null;
        buf = [];
      }
      continue;
    }

    const open = line.match(/^( {0,3})(`{3,}|~{3,})(.*)$/);
    if (open) {
      const marker = open[2];
      const info = open[3] || "";
      if (marker[0] === "`" && info.includes("`")) {
        out.push(line);
        continue;
      }
      fence = { char: marker[0], len: marker.length };
      buf = [line];
      continue;
    }

    out.push(line);
  }

  if (fence) {
    fences.push(buf.join("\n"));
    out.push(`\0FENCE${fences.length - 1}\0`);
  }

  return {
    text: out.join("\n"),
    restore(value) {
      return value.replace(/\0FENCE(\d+)\0/g, (_, index) => fences[Number(index)]);
    },
  };
}

function convertDetails(src) {
  let i = 0;
  let out = "";

  while (i < src.length) {
    const atLineStart = i === 0 || src.charCodeAt(i - 1) === 10;
    const open = atLineStart
      ? src.slice(i).match(/^( {0,3})<details(\s[^>]*)?>/i)
      : null;

    if (!open) {
      out += src[i];
      i += 1;
      continue;
    }

    const innerStart = i + open[0].length;
    const closeAt = findMatchingClose(src, innerStart);
    if (closeAt < 0) {
      out += src[i];
      i += 1;
      continue;
    }

    const inner = convertDetails(src.slice(innerStart, closeAt));
    out += toBbcode(open[2] || "", inner);
    i = closeAt + CLOSE_TAG.length;
  }

  return out;
}

function findMatchingClose(src, innerStart) {
  const re = /<\/?details\b/gi;
  re.lastIndex = innerStart;
  let depth = 1;
  let match;

  while ((match = re.exec(src))) {
    if (match[0].charAt(1) === "/") {
      depth -= 1;
      if (depth === 0) {
        return match.index;
      }
    } else {
      depth += 1;
    }
  }

  return -1;
}

function toBbcode(attrs, inner) {
  const open = /\bopen\b/i.test(attrs) ? " open" : "";
  const summary = inner.match(
    /^\s*<summary(\s[^>]*)?>([\s\S]*?)<\/summary>\s*/i
  );
  let title = "";
  let body = inner.replace(/^\s+|\s+$/g, "");

  if (summary) {
    title = stripTags(summary[2]).replace(/"/g, "“").trim();
    body = inner.slice(summary[0].length).replace(/^\s+/, "").replace(/\s+$/, "");
  }

  if (title) {
    return `[details="${title}"${open}]\n${body}\n[/details]`;
  }

  return `[details${open}]\n${body}\n[/details]`;
}

function stripTags(html) {
  return html.replace(/<[^>]+>/g, "");
}
